import {
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  InboxStackIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, SVGProps, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '~/routing';
import { getHlxNavIcon } from '~/routing/hlxNavIcons';
import { JobListing } from '~/types/hlx/jobListing';
import { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { MsaasNegotiation } from '~/types/hlx/msaasNegotiation';
import type { JobListingResponseSummary } from '../api';
import {
  fetchB2BListings,
  fetchEmployerNegotiations,
  fetchJobListingResponses,
  fetchJobListings,
} from '../api';

type Props = { employerId: string };

type Stat = { label: string; value: number; tone: 'primary' | 'muted' | 'warning' | 'success' };

type CardProps = {
  title: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  stats: Stat[];
  onClick: () => void;
};

const toneClasses: Record<Stat['tone'], string> = {
  primary: 'text-grey-100',
  muted: 'text-grey-300',
  warning: 'text-warning',
  success: 'text-success',
};

const StatCard = ({ title, Icon, stats, onClick }: CardProps) => (
  <button
    type="button"
    onClick={onClick}
    className="bg-white rounded-xl shadow-sm border border-grey-900 p-6 w-full text-left hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-2 mb-4">
      <Icon className="size-5 text-primary-50" />
      <h3 className="sub-heading-sb text-grey-100">{title}</h3>
      <span className="ml-auto caption-text text-primary-50">View all →</span>
    </div>
    <div className="grid grid-cols-3 gap-4">
      {stats.map((stat) => (
        <div key={stat.label}>
          <p className={`text-2xl font-bold ${toneClasses[stat.tone]}`}>{stat.value}</p>
          <p className="body-text text-grey-500">{stat.label}</p>
        </div>
      ))}
    </div>
  </button>
);

const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm border border-grey-900 p-6 animate-pulse">
    <div className="h-5 bg-grey-900 rounded w-1/3 mb-4" />
    <div className="grid grid-cols-3 gap-4">
      <div className="h-12 bg-grey-900 rounded" />
      <div className="h-12 bg-grey-900 rounded" />
      <div className="h-12 bg-grey-900 rounded" />
    </div>
  </div>
);

type SectionData = {
  jobListings: JobListing[];
  jobResponses: JobListingResponseSummary[];
  b2bListings: MsaasJobListing[];
  b2bNegotiations: MsaasNegotiation[];
};

const EMPTY_DATA: SectionData = {
  jobListings: [],
  jobResponses: [],
  b2bListings: [],
  b2bNegotiations: [],
};

export const JobListingsSection = ({ employerId }: Props) => {
  const navigate = useNavigate();
  const [data, setData] = useState<SectionData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employerId) {
      setData(EMPTY_DATA);
      setLoading(false);
      return;
    }
    let isCancelled = false;
    setLoading(true);

    // One orchestrated fetch keeps the four cards in lockstep: they all
    // commit at the same time instead of three skeletons popping in
    // independently.
    //
    // `allSettled` (not `all`) — a single failed fetch should only zero its
    // own dataset, not blank every card. A B2B outage shouldn't make the
    // user think they have no job listings, and vice versa.
    Promise.allSettled([
      fetchJobListings(employerId),
      fetchJobListingResponses(),
      fetchB2BListings(employerId),
      fetchEmployerNegotiations(employerId),
    ]).then(([jobListingsResult, jobResponsesResult, b2bListingsResult, b2bNegotiationsResult]) => {
      if (isCancelled) return;
      setData({
        jobListings: jobListingsResult.status === 'fulfilled' ? jobListingsResult.value : [],
        jobResponses: jobResponsesResult.status === 'fulfilled' ? jobResponsesResult.value : [],
        b2bListings: b2bListingsResult.status === 'fulfilled' ? b2bListingsResult.value : [],
        b2bNegotiations:
          b2bNegotiationsResult.status === 'fulfilled' ? b2bNegotiationsResult.value : [],
      });
      setLoading(false);
    });
    return () => {
      isCancelled = true;
    };
  }, [employerId]);

  // Mobile (single column) groups cards by category: Job Listings + Job
  // Responses together, then B2B Listings + B2B Responses together — so
  // related stats stay adjacent on a narrow viewport. Desktop reflows into
  // a 2×2 grid where left column = Job, right column = B2B, row 1 =
  // listings, row 2 = responses. The reflow is driven by switching grid
  // flow from default `row` to `col` at the `md` breakpoint, so the same
  // DOM order yields category-grouped mobile *and* listings/responses
  // desktop without duplicating children.
  const gridClasses = 'grid grid-cols-1 md:grid-cols-2 md:grid-rows-2 md:grid-flow-col gap-4';

  if (loading || !data) {
    return (
      <div className={gridClasses}>
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className={gridClasses}>
      {/* Job column: listings then responses */}
      <StatCard
        title="Job Listings"
        Icon={getHlxNavIcon(paths.hlx.listings) ?? ClipboardDocumentListIcon}
        stats={providerListingStats(data.jobListings)}
        onClick={() => navigate(paths.hlx.listings)}
      />
      <StatCard
        title="Job Responses"
        Icon={getHlxNavIcon(paths.hlx.jobOpportunities) ?? InboxStackIcon}
        stats={providerResponseStats(data.jobResponses)}
        onClick={() => navigate(paths.hlx.jobOpportunities)}
      />

      {/* B2B column: listings then responses */}
      <StatCard
        title="B2B Listings"
        Icon={getHlxNavIcon(paths.hlx.msaasListings) ?? DocumentTextIcon}
        stats={b2bListingStats(data.b2bListings)}
        onClick={() => navigate(paths.hlx.msaasListings)}
      />
      <StatCard
        title="B2B Responses"
        Icon={getHlxNavIcon(paths.hlx.b2bOpportunities) ?? ScaleIcon}
        stats={b2bResponseStats(data.b2bNegotiations)}
        onClick={() => navigate(paths.hlx.b2bOpportunities)}
      />
    </div>
  );
};

const providerListingStats = (listings: JobListing[]): Stat[] => {
  const active = listings.filter((l) => l.activeFlag && !l.filledFlag).length;
  const draft = listings.filter((l) => !l.activeFlag && !l.filledFlag).length;
  const filled = listings.filter((l) => !!l.filledFlag).length;
  return [
    { label: 'Active', value: active, tone: 'primary' },
    { label: 'Draft', value: draft, tone: 'muted' },
    { label: 'Filled', value: filled, tone: 'warning' },
  ];
};

const b2bListingStats = (listings: MsaasJobListing[]): Stat[] => {
  const active = listings.filter((l) => l.activeFlag && !l.filledFlag).length;
  const draft = listings.filter((l) => !l.activeFlag && !l.filledFlag).length;
  const filled = listings.filter((l) => !!l.filledFlag).length;
  return [
    { label: 'Active', value: active, tone: 'primary' },
    { label: 'Draft', value: draft, tone: 'muted' },
    { label: 'Filled', value: filled, tone: 'warning' },
  ];
};

// APPLIED = provider applied, employer hasn't engaged yet; NEGOTIATING = active
// back-and-forth. Same semantics used by the B2B response stats below so both
// response cards tell the same story with the same labels.
const providerResponseStats = (responses: JobListingResponseSummary[]): Stat[] => {
  const open = responses.filter((r) => r.status === 'APPLIED' || r.status === 'NEGOTIATING').length;
  const needsAction = responses.filter((r) => r.status === 'APPLIED').length;
  const pending = responses.filter((r) => r.status === 'NEGOTIATING').length;
  return [
    { label: 'Open', value: open, tone: 'primary' },
    { label: 'Needs action', value: needsAction, tone: 'warning' },
    { label: 'Pending', value: pending, tone: 'muted' },
  ];
};

const b2bResponseStats = (negotiations: MsaasNegotiation[]): Stat[] => {
  const open = negotiations.filter(
    (n) => n.jobResponse.status === 'APPLIED' || n.jobResponse.status === 'NEGOTIATING'
  ).length;
  const needsAction = negotiations.filter((n) => n.jobResponse.status === 'APPLIED').length;
  const pending = negotiations.filter((n) => n.jobResponse.status === 'NEGOTIATING').length;
  return [
    { label: 'Open', value: open, tone: 'primary' },
    { label: 'Needs action', value: needsAction, tone: 'warning' },
    { label: 'Pending', value: pending, tone: 'muted' },
  ];
};
